// @ai-generated 2026-09-15T09:59:29+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
import { type ApiContainerAction, getEnumContainerOrderActionLabel } from '@Model';
import { type PlannedContainerGroup, toContainerActionGroups } from '@Modules/Orders';
import { FrameEditContainerType } from '@Modules/Stammdaten';
import { Badge, FlexColumn, LinkedCell, UnlinkedCell, useOnTheFlyModal } from '@synqo/frontend-component-library';
import { type FC, memo, useCallback, useMemo, useRef } from 'react';
import styles from './LinkedContainerActionsCell.module.css';

export interface LinkedContainerActionsCellProps {
    containerActions: readonly ApiContainerAction[];
}

interface ContainerActionItemProps {
    group: PlannedContainerGroup;
    onOpenContainerTypeModal: (containerTypeId: string) => void;
}

const ContainerActionItem: FC<ContainerActionItemProps> = memo(function ContainerActionItem({
    group,
    onOpenContainerTypeModal,
}: ContainerActionItemProps) {
    const { id, action, label, quantity } = group;

    const handleClick = useCallback(() => {
        onOpenContainerTypeModal(id);
    }, [id, onOpenContainerTypeModal]);

    return (
        <span className={styles.entry}>
            <span className={styles.action}>
                <Badge variant="default">{getEnumContainerOrderActionLabel(action)}</Badge>
            </span>

            {quantity > 1 && <span className={styles.quantity}>{`${quantity} ×`}</span>}

            {/* A staged order may name a type the master data does not know yet; that one has nothing to open. */}
            {id ? <LinkedCell label={label} itemId={id} onClick={handleClick} /> : <UnlinkedCell label={label} />}
        </span>
    );
});

/**
 * The container actions of an order as one table cell: what is delivered and collected, by type.
 *
 * Mirrors the service column beside it — one line per entry, the type opening its master data record —
 * because both columns are read at a glance while scanning the list. The lines are the groups the
 * order's own container card shows, counted the same way.
 */
export const LinkedContainerActionsCell: FC<LinkedContainerActionsCellProps> = memo(
    function LinkedContainerActionsCell({ containerActions }: LinkedContainerActionsCellProps) {
        const modalIdRef = useRef('');
        const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

        const closeModal = useCallback(() => {
            if (modalIdRef.current) {
                closeOnTheFlyModals(modalIdRef.current);
                modalIdRef.current = '';
            }
        }, [closeOnTheFlyModals]);

        const handleOpenContainerTypeModal = useCallback(
            (containerTypeId: string) => {
                modalIdRef.current = openOnTheFlyModal({
                    child: (
                        <FrameEditContainerType
                            primaryKey={containerTypeId}
                            canEdit={false}
                            canCreate={false}
                            canDelete={false}
                            events={{
                                onClose: closeModal,
                            }}
                        />
                    ),
                });
            },
            [openOnTheFlyModal, closeModal],
        );

        const groups = useMemo(() => toContainerActionGroups(containerActions), [containerActions]);

        return (
            <FlexColumn gap="xxs">
                {groups.map((group) => (
                    <ContainerActionItem
                        key={group.key}
                        group={group}
                        onOpenContainerTypeModal={handleOpenContainerTypeModal}
                    />
                ))}
            </FlexColumn>
        );
    },
);
